'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import type { SchoolYear } from '@/types/api';
import { useCreateSchoolYear, useUpdateSchoolYear } from '@/hooks/use-schools';
import { validateDateOrder } from '@/lib/validation';

interface SchoolYearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolYear?: SchoolYear | null;
  schoolId: string;
  schoolPrimaryColor?: string;
  onSuccess: () => void;
}

export function SchoolYearDialog({
  open,
  onOpenChange,
  schoolYear,
  schoolId,
  schoolPrimaryColor = '#1e40af',
  onSuccess,
}: SchoolYearDialogProps) {
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();

  const createMutation = useCreateSchoolYear(schoolId);
  const updateMutation = useUpdateSchoolYear(schoolId);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };

  // Helper to parse date-only strings as local dates (avoid timezone issues)
  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Initialize dates when dialog opens or schoolYear changes
  useEffect(() => {
    if (open) {
      if (schoolYear) {
        if (schoolYear.startDate) setStartDate(parseLocalDate(schoolYear.startDate));
        if (schoolYear.endDate) setEndDate(parseLocalDate(schoolYear.endDate));
      } else {
        // New school year - clear dates
        setStartDate(undefined);
        setEndDate(undefined);
      }
    }
  }, [open, schoolYear]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      return;
    }

    const dateError = validateDateOrder(startDate, endDate);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    try {
      if (schoolYear) {
        // Update existing school year
        await updateMutation.mutateAsync({
          schoolYearId: schoolYear.id,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          current: schoolYear.current,
        });
      } else {
        // Create new school year
        await createMutation.mutateAsync({
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
        });
      }
      
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      // Error handling is done in the hooks
      console.error('Failed to save school year:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle style={{ color: schoolPrimaryColor }}>
            {schoolYear ? 'Edit School Year' : 'Add School Year'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <DatePicker
                date={startDate}
                onSelect={setStartDate}
                placeholder="Select start date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <DatePicker
                date={endDate}
                onSelect={setEndDate}
                minDate={startDate}
                disabled={(date) => startDate ? date < startDate : false}
                placeholder="Select end date"
              />
            </div>
          </div>
          
          {schoolYear && (
            <div className="text-sm text-muted-foreground">
              {schoolYear.current && (
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">
                  Current School Year
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !startDate || !endDate}
            style={{ 
              backgroundColor: schoolPrimaryColor,
              color: getContrastColor(schoolPrimaryColor)
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              schoolYear ? 'Update' : 'Create'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

